import React, { useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  calculateTransactionFee,
  currencyFormat,
  formatPrice,
  getSellerPayoutBreakdown,
} from "../../utils/utils";

interface OrderTotalsSummaryProps {
  orders: Record<string, Record<string, any[]>>;
  /** Seller sell-history view: show payout-related totals, hide buyer-only fields */
  forSeller?: boolean;
  /** Optional label for the active date filter, e.g. "03/21/2026 – 04/04/2026" */
  periodLabel?: string;
}

const emptyTotals = {
  paymentsSeller: 0,
  sellerFedIns: 0,
  sellerTradeNetRx: 0,
  stripeTransactionFee: 0,
  stripeCardFee: 0,
  buyerShipping: 0,
  sellerShippingDeducted: 0,
  buyerPayment: 0,
};

const getTotalAmount = (sellerOrders: any[]) => {
  const filteredOrders = sellerOrders.filter((order: any) => order.status === 1);
  let total = 0;
  filteredOrders.forEach((item: any) => {
    total = total + Number(item.totalAmount);
  });
  return formatPrice(total);
};

const getTotalShippingFee = (sellerOrders: any[]) => {
  const filteredOrders = sellerOrders.filter((order: any) => order.status === 1);
  let total = 0;
  filteredOrders.forEach((item: any) => {
    total = total + Number(item.shippingFee);
  });
  return total.toFixed(2);
};

const getFedexInsurance = (sellerOrders: any[]) => {
  const filteredOrders = sellerOrders.filter((order: any) => order.status === 1);
  return filteredOrders[0]?.fedExInsurance || "0";
};

const getSellerTotal = (sellerOrders: any[]) => {
  const breakdown = getSellerPayoutBreakdown(
    sellerOrders.filter((order: any) => order.status === 1)
  );
  if (!breakdown) return formatPrice(0);
  return formatPrice(breakdown.creditTotal);
};

const computeTotals = (orders: Record<string, Record<string, any[]>>) => {
  let total = { ...emptyTotals };

  try {
    if (!orders || typeof orders !== "object") {
      return total;
    }

    Object.keys(orders).forEach((orderKey: string) => {
      const group = orders[orderKey];
      if (!group || typeof group !== "object") return;

      Object.keys(group)
        .sort()
        .forEach((seller: string) => {
          const orderSeller = group[seller];
          if (!Array.isArray(orderSeller) || orderSeller.length === 0) return;

          try {
            const pb = Number(getTotalAmount(orderSeller)) || 0;
            const breakdown = getSellerPayoutBreakdown(
              orderSeller.filter((order: any) => order.status === 1)
            );
            const ps =
              Number(breakdown?.creditTotal ?? getSellerTotal(orderSeller)) || 0;
            const fedIns =
              Number(
                breakdown?.fedExInsurance ?? getFedexInsurance(orderSeller)
              ) || 0;
            const trNetRx =
              Number(
                breakdown?.tradeNetFee ??
                  (pb * Number(orderSeller[0]?.tradeNetFee || 0)) / 100
              ) || 0;
            const trFee =
              Number(
                breakdown?.transactionFee ?? calculateTransactionFee(pb)
              ) || 0;
            const cardFee = Number(breakdown?.stripeCardFee ?? 0) || 0;
            const shipping = Number(getTotalShippingFee(orderSeller)) || 0;
            const shippingDeducted =
              Number(breakdown?.shippingFeeDeducted ?? 0) || 0;

            total = {
              paymentsSeller: total.paymentsSeller + ps,
              sellerFedIns: total.sellerFedIns + fedIns,
              sellerTradeNetRx: total.sellerTradeNetRx + trNetRx,
              stripeTransactionFee: total.stripeTransactionFee + trFee,
              stripeCardFee: total.stripeCardFee + cardFee,
              buyerShipping: total.buyerShipping + shipping,
              sellerShippingDeducted:
                total.sellerShippingDeducted + shippingDeducted,
              buyerPayment: total.buyerPayment + pb,
            };
          } catch {
            // Skip malformed groups so the order list still renders
          }
        });
    });
  } catch {
    return { ...emptyTotals };
  }

  return total;
};

export const OrderTotalsSummary: React.FunctionComponent<OrderTotalsSummaryProps> = ({
  orders,
  forSeller = false,
  periodLabel,
}) => {
  const totalData = useMemo(() => computeTotals(orders), [orders]);
  const itemSx = { fontSize: 12, lineHeight: 1.3 };

  const periodNote = (
    <Box sx={{ px: 2, pt: 1 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {periodLabel
          ? `Totals for selected date range: ${periodLabel}`
          : "Totals are for the selected period in the date filter above."}
      </Typography>
    </Box>
  );

  if (forSeller) {
    return (
      <Box>
        {periodNote}
        <Grid container sx={{ p: 2, fontSize: 12 }} spacing={0}>
          <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
            {`Total Item(s) Sales: `}
            <p style={{ margin: "4px 0 0", fontSize: 13 }}>
              {currencyFormat(totalData.buyerPayment)}
            </p>
          </Grid>
          {totalData.sellerShippingDeducted > 0 && (
            <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
              {`Total Shipping Fee: `}
              <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                {currencyFormat(totalData.sellerShippingDeducted)}
              </p>
            </Grid>
          )}
          <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
            {`Total Transaction fee: `}
            <p style={{ margin: "4px 0 0", fontSize: 13 }}>
              {currencyFormat(totalData.stripeTransactionFee)}
            </p>
          </Grid>
          <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
            {`Total Card processing fee: `}
            <p style={{ margin: "4px 0 0", fontSize: 13 }}>
              {currencyFormat(totalData.stripeCardFee)}
            </p>
          </Grid>
          <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
            {`Total FedEx insurance: `}
            <p style={{ margin: "4px 0 0", fontSize: 13 }}>
              {currencyFormat(totalData.sellerFedIns)}
            </p>
          </Grid>
          <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
            {`Total Credit: `}
            <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 700 }}>
              {currencyFormat(totalData.paymentsSeller)}
            </p>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {periodNote}
      <Grid container sx={{ p: 2, fontSize: 12 }} spacing={0}>
        <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
          {`Total Buyer payments: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.buyerPayment)}
          </p>
        </Grid>
        <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
          {`Total Buyer shipping Fee: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.buyerShipping)}
          </p>
        </Grid>
        <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
          {`Total Stripe transaction fee: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.stripeTransactionFee)}
          </p>
        </Grid>
        <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
          {`Total Transaction fee: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.stripeCardFee)}
          </p>
        </Grid>
        <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
          {`Total Seller tradeNetRx fee: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.sellerTradeNetRx)}
          </p>
        </Grid>
        <Grid item xs={6} sm={6} md={2} lg={2} sx={itemSx}>
          {`Total Seller fedex insurance: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.sellerFedIns)}
          </p>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} sx={{ ...itemSx, pt: 2, mt: 1 }}>
          {`Total Payments to Seller: `}
          <p style={{ margin: "4px 0 0", fontSize: 13 }}>
            {currencyFormat(totalData.paymentsSeller)}
          </p>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderTotalsSummary;
